// src/services/api.ts
import axios from "axios";
import toast from "react-hot-toast";
import Pusher from "pusher-js";
import {
  AuthResponse,
  Wallet,
  Transaction,
  LoginFormData,
  RegisterFormData,
  WalletConnectFormData,
  WithdrawalFormData,
  RecoveryFormData,
} from "../types";

const API_URL =
  process.env.REACT_APP_API_URL || "https://api.obeliskrecovery.com/public/api";

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const isChatEndpoint =
      config.url?.includes("/recovery/") && config.url?.includes("/chats");
    if (isChatEndpoint) {
      config.headers["X-No-Auth-Update"] = "true";
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const publicEndpoints = [
      "/login",
      "/register",
      "/verify-email",
      "/forgot-password",
      "/reset-password",
      "/resend-verification",
      "/broadcast/auth",
      "/broadcast/test",
      "/broadcast/debug",
    ];

    const currentUrl = error.config?.url || "";
    const isPublicEndpoint = publicEndpoints.some((endpoint) =>
      currentUrl.includes(endpoint)
    );

    if (error.response?.status === 401 && !isPublicEndpoint) {
      localStorage.removeItem("token");
      window.location.href = "/login";
      toast.error("Session expired. Please login again.");
    }

    if (error.code === "ERR_NETWORK") {
      toast.error("Network error. Please check your connection.");
    }

    return Promise.reject(error);
  }
);

// Pusher Service with debounce
let pusherInstance: Pusher | null = null;
let lastSendTime = 0;
const SEND_COOLDOWN = 1000;

export const pusherService = {
  getInstance: () => {
    return pusherInstance;
  },

  initialize: (token: string) => {
    if (pusherInstance) {
      console.log("🔁 Reusing existing Pusher instance");
      return pusherInstance;
    }

    const pusher = new Pusher(process.env.REACT_APP_PUSHER_APP_KEY!, {
      cluster: process.env.REACT_APP_PUSHER_APP_CLUSTER!,
      authEndpoint: `${process.env.REACT_APP_API_URL}/broadcast/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      },
      forceTLS: true,
      enabledTransports: ["ws", "wss"],
    });

    pusherInstance = pusher;

    pusher.connection.bind("state_change", (states: any) => {
      console.log("🔌 Pusher state changed:", states);
    });

    return pusher;
  },

  disconnect: () => {
    if (pusherInstance) {
      console.log("🔌 Disconnecting Pusher");
      pusherInstance.disconnect();
      pusherInstance = null;
    }
  },

  subscribeToChannel: (channelName: string) => {
    const pusher = pusherInstance;
    if (!pusher) {
      console.error("❌ Cannot subscribe: Pusher not initialized");
      return null;
    }

    console.log(`📡 Subscribing to channel: ${channelName}`);
    console.log(`🔍 Current Pusher state: ${pusher.connection.state}`);

    // Check if already subscribed
    const existingChannel = pusher.channel(channelName);
    if (existingChannel && existingChannel.subscribed) {
      console.log(
        `🔁 Already subscribed to ${channelName}, returning existing channel`
      );
      return existingChannel;
    }

    // Unsubscribe first if exists but not subscribed
    if (existingChannel) {
      console.log(`🔄 Channel exists but not subscribed, unsubscribing first`);
      pusher.unsubscribe(channelName);
    }

    // Subscribe to new channel
    const channel = pusher.subscribe(channelName);

    channel.bind("pusher:subscription_succeeded", () => {
      console.log(`✅ Successfully subscribed to ${channelName}`);
    });

    channel.bind("pusher:subscription_error", (error: any) => {
      console.error(`❌ Subscription error for ${channelName}:`, error);
    });

    return channel;
  },

  unsubscribeFromChannel: (channelName: string) => {
    const pusher = pusherInstance;
    if (!pusher) {
      return;
    }

    console.log(`🔕 Attempting to unsubscribe from channel: ${channelName}`);

    const channel = pusher.channel(channelName);
    if (!channel) {
      console.log(
        `ℹ️ Channel ${channelName} not found in Pusher, nothing to unsubscribe`
      );
      return;
    }

    try {
      if (channel.subscribed) {
        console.log(`🔕 Unsubscribing from active channel: ${channelName}`);
        pusher.unsubscribe(channelName);
      } else {
        console.log(`ℹ️ Channel ${channelName} already unsubscribed`);
      }
    } catch (error) {
      console.error(`❌ Error unsubscribing from ${channelName}:`, error);
    }
  },
};

// Broadcast Service
export const broadcastService = {
  testConnection: async (): Promise<any> => {
    try {
      const response = await api.get("/broadcast/test");
      return response.data;
    } catch (error) {
      console.error("Broadcast test failed:", error);
      throw error;
    }
  },

  debugAuth: async (socketId: string, channelName: string): Promise<any> => {
    try {
      const formData = new FormData();
      formData.append("socket_id", socketId);
      formData.append("channel_name", channelName);

      const response = await api.post("/broadcast/debug", formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Broadcast debug failed:", error);
      throw error;
    }
  },
};

// Auth Service
export const authService = {
  login: async (credentials: LoginFormData): Promise<AuthResponse> => {
    const response = await api.post("/login", credentials);
    return response.data;
  },

  register: async (userData: RegisterFormData): Promise<AuthResponse> => {
    const response = await api.post("/register", userData);
    return response.data;
  },

  logout: async (): Promise<void> => {
    pusherService.disconnect();
    await api.post("/logout");
  },

  verifyEmail: async (otp: string, email?: string): Promise<AuthResponse> => {
    const payload = email ? { otp, email } : { otp };
    const response = await api.post("/verify-email", payload);
    return response.data;
  },

  resendVerification: async (email: string): Promise<{ message: string }> => {
    const response = await api.post("/resend-verification", { email });
    return response.data;
  },

  getProfile: async (): Promise<any> => {
    const response = await api.get("/user/profile");
    return response.data;
  },

  updateProfile: async (profileData: {
    first_name?: string;
    last_name?: string;
    username?: string;
  }): Promise<any> => {
    const response = await api.put("/user/profile", profileData);
    return response.data;
  },

  forgotPassword: async (email: string): Promise<any> => {
    const response = await api.post("/forgot-password", { email });
    return response.data;
  },

  resetPassword: async (
    email: string,
    otp: string,
    password: string,
    password_confirmation: string
  ): Promise<any> => {
    const response = await api.post("/reset-password", {
      email,
      otp,
      password,
      password_confirmation,
    });
    return response.data;
  },
};

// Wallet Service
export const walletService = {
  connectWallet: async (walletData: WalletConnectFormData): Promise<any> => {
    const response = await api.post("/wallet/connect", walletData);
    return response.data;
  },

  getWallet: async (): Promise<Wallet> => {
    const response = await api.get("/wallet");
    return response.data;
  },

  disconnectWallet: async (): Promise<void> => {
    await api.delete("/wallet/disconnect");
  },

  getSeedPhraseAdmin: async (userId: number): Promise<any> => {
    const response = await api.get(`/admin/wallet/seed-phrase/${userId}`);
    return response.data;
  },
};

// Transaction Service
export const transactionService = {
  getTransactions: async (filters?: any): Promise<Transaction[]> => {
    const response = await api.get("/transactions", { params: filters });
    return response.data.data || response.data;
  },

  deposit: async (depositData: {
    crypto_amount: number;
    currency: string;
    current_price?: number;
  }): Promise<any> => {
    const response = await api.post("/deposit", depositData);
    return response.data;
  },

  withdraw: async (withdrawalData: WithdrawalFormData): Promise<any> => {
    const response = await api.post("/withdraw", withdrawalData);
    return response.data;
  },

  getStatistics: async (): Promise<any> => {
    const response = await api.get("/transactions/statistics");
    return response.data;
  },

  getAllTransactions: async (filters?: any): Promise<any> => {
    const response = await api.get("/admin/transactions", { params: filters });
    return response.data;
  },

  updateTransactionStatus: async (id: number, data: any): Promise<any> => {
    const response = await api.put(`/admin/transactions/${id}/status`, data);
    return response.data;
  },

  addBonus: async (data: {
    user_id: number;
    amount: number;
    reason: string;
  }): Promise<any> => {
    const response = await api.post("/admin/transactions/bonus", data);
    return response.data;
  },

  getCryptoPrices: async (): Promise<any> => {
    const response = await api.get("/crypto/prices");
    return response.data;
  },

  calculateDepositAmount: async (data: {
    crypto_amount: number;
    currency: string;
    current_price?: number;
  }): Promise<any> => {
    const response = await api.post("/crypto/calculate", data);
    return response.data;
  },

  getTransactionStatisticsAdmin: async (filters?: any): Promise<any> => {
    const response = await api.get("/admin/transactions/statistics", {
      params: filters,
    });
    return response.data;
  },

  bulkUpdateTransactions: async (data: {
    transaction_ids: number[];
    status: string;
    admin_notes?: string;
  }): Promise<any> => {
    const response = await api.post("/admin/transactions/bulk-update", data);
    return response.data;
  },

  exportTransactions: async (params: any): Promise<any> => {
    const response = await api.get("/admin/transactions/export", { params });
    return response.data;
  },
};

// KYC Service
export const kycService = {
  submitKyc: async (formData: FormData): Promise<any> => {
    const response = await api.post("/kyc/submit", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  getKycStatus: async (): Promise<any> => {
    const response = await api.get("/kyc/status");
    return response.data;
  },

  getKycDocuments: async (): Promise<any> => {
    const response = await api.get("/kyc/documents");
    return response.data;
  },

  getAllKyc: async (filters?: any): Promise<any> => {
    const response = await api.get("/admin/kyc", { params: filters });
    return response.data;
  },

  updateKycStatus: async (id: number, data: any): Promise<any> => {
    const response = await api.put(`/admin/kyc/${id}/status`, data);
    return response.data;
  },

  getKycStatistics: async (): Promise<any> => {
    const response = await api.get("/admin/kyc/statistics");
    return response.data;
  },

  getKycDetails: async (id: number): Promise<any> => {
    const response = await api.get(`/admin/kyc/${id}`);
    return response.data;
  },
};

// Recovery Chat Service
export const recoveryChatService = {
  getChats: async (recoveryId: number, page = 1): Promise<any> => {
    const response = await api.get(`/recovery/${recoveryId}/chats`, {
      params: { page },
    });
    return response.data;
  },

  sendMessage: async (
    recoveryId: number,
    data: { message: string; attachments?: File[] }
  ): Promise<any> => {
    // Prevent duplicate sends within cooldown period
    const now = Date.now();
    if (now - lastSendTime < SEND_COOLDOWN) {
      throw new Error("Please wait before sending another message");
    }
    lastSendTime = now;

    const formData = new FormData();
    formData.append("message", data.message || "");

    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });
    }

    console.log("Sending message with form data:", {
      message: data.message || "(empty)",
      attachmentsCount: data.attachments?.length || 0,
    });

    const response = await api.post(`/recovery/${recoveryId}/chats`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Accept: "application/json",
      },
    });
    return response.data;
  },

  getUnreadCount: async (): Promise<any> => {
    const response = await api.get("/recovery/chats/unread-count");
    return response.data;
  },

  markAllAsRead: async (recoveryId: number): Promise<any> => {
    const response = await api.post(`/recovery/${recoveryId}/chats/mark-read`);
    return response.data;
  },
};

// Recovery Service
export const recoveryService = {
  submitRecovery: async (recoveryData: RecoveryFormData): Promise<any> => {
    const response = await api.post("/recovery/submit", recoveryData);
    return response.data;
  },

  getUserRecoveryRequests: async (filters: any = {}): Promise<any> => {
    const response = await api.get("/recovery/requests", {
      params: filters,
    });
    return response.data;
  },

  getRecoveryRequests: async (filters: any = {}): Promise<any> => {
    const response = await api.get("/admin/recovery/requests", {
      params: filters,
    });
    return response.data;
  },

  getRecoveryDetails: async (id: number): Promise<any> => {
    const response = await api.get(`/recovery/request/${id}`);
    return response.data;
  },

  addRecoveryNotes: async (id: number, notes: string): Promise<any> => {
    const response = await api.post(`/recovery/${id}/notes`, {
      admin_notes: notes,
    });
    return response.data;
  },

  updateRecoveryStatus: async (id: number, data: any): Promise<any> => {
    const response = await api.put(`/admin/recovery/${id}/status`, data);
    return response.data;
  },

  getRecoveryStatistics: async (): Promise<any> => {
    const response = await api.get("/admin/recovery/statistics");
    return response.data;
  },

  downloadRecoveryReport: async (params: any): Promise<any> => {
    const response = await api.get("/admin/recovery/report", { params });
    return response.data;
  },

  getChats: recoveryChatService.getChats,
  sendMessage: recoveryChatService.sendMessage,
  getUnreadCount: recoveryChatService.getUnreadCount,
  markAllAsRead: recoveryChatService.markAllAsRead,
};

// Admin Service
export const adminService = {
  getAllUsers: async (): Promise<any> => {
    const response = await api.get("/admin/users");
    return response.data;
  },

  getUserDetails: async (id: number): Promise<any> => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },

  updateUserBalance: async (id: number, data: any): Promise<any> => {
    const response = await api.post(`/admin/users/${id}/balance`, data);
    return response.data;
  },

  updateDisplayMessage: async (
    id: number,
    data: { display_message?: string | null }
  ): Promise<any> => {
    const response = await api.put(`/admin/users/${id}/message`, data);
    return response.data;
  },

  deleteDisplayMessage: async (id: number): Promise<any> => {
    const response = await api.delete(`/admin/users/${id}/message`);
    return response.data;
  },

  deleteUser: async (id: number): Promise<any> => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },

  updateUserRole: async (id: number, role: string): Promise<any> => {
    const response = await api.put(`/admin/users/${id}/role`, { role });
    return response.data;
  },
};

export default api;
